import { useState } from 'react'
import { Copy, KeyRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect } from '@/components/ui/native-select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import { apiErrorMessage, teamService, TeamMember } from '@/services/team-service'
import * as toast from '@/lib/toast'

interface CreateApiKeyDialogProps {
  agents: TeamMember[]
  onCreated: () => void
}

/** Cria a chave do agente e mostra o valor UMA vez (o servidor guarda só o hash). */
export function CreateApiKeyDialog({ agents, onCreated }: CreateApiKeyDialogProps) {
  const [open, setOpen] = useState(false)
  const [userId, setUserId] = useState('')
  const [label, setLabel] = useState('')
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reset = () => {
    setUserId('')
    setLabel('')
    setCreatedKey(null)
  }

  const create = async () => {
    setBusy(true)
    try {
      const created = await teamService.createApiKey(userId || agents[0]?.id, label)
      setCreatedKey(created.key)
      onCreated()
    } catch (error) {
      toast.error(apiErrorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  const copy = async () => {
    if (!createdKey) return
    try {
      await navigator.clipboard.writeText(createdKey)
      toast.success('Chave copiada')
    } catch {
      toast.error('Não foi possível copiar; selecione e copie manualmente')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { setOpen(next); if (!next) reset() }}>
      <DialogTrigger asChild>
        <Button disabled={agents.length === 0}>
          <KeyRound /> Nova chave
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{createdKey ? 'Chave criada' : 'Nova chave de API'}</DialogTitle>
          <DialogDescription>
            {createdKey
              ? 'Copie agora: ela não será mostrada de novo. Use no header X-Api-Key.'
              : 'Só usuários com papel Agente podem ter chave.'}
          </DialogDescription>
        </DialogHeader>
        {createdKey ? (
          <div className="flex items-center gap-2">
            <Input readOnly value={createdKey} aria-label="Chave de API" className="font-mono text-xs" onFocus={(e) => e.target.select()} />
            <Button variant="outline" size="icon" aria-label="Copiar chave" onClick={copy}>
              <Copy />
            </Button>
          </div>
        ) : (
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="api-key-agent">Agente</Label>
              <NativeSelect id="api-key-agent" value={userId || agents[0]?.id || ''} onChange={(e) => setUserId(e.target.value)}>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>{agent.fullName}</option>
                ))}
              </NativeSelect>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="api-key-label">Nome da chave</Label>
              <Input id="api-key-label" placeholder="Ex.: Hermes na VPS" value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
          </div>
        )}
        <DialogFooter>
          {createdKey ? (
            <Button onClick={() => setOpen(false)}>Concluir</Button>
          ) : (
            <Button loading={busy} disabled={!label.trim() || agents.length === 0} onClick={create}>
              Criar chave
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
